import { errorCauseFingerprint } from "@aircap/common/errorFingerprint";
import { isExpectedUserError, isExpectedUserSentryEvent } from "@aircap/common/expectedUserError";
import * as Sentry from "@sentry/vue";
import { ReCaptchaEnterpriseProvider } from "firebase/app-check";
import "unfonts.css";
import { createApp } from "vue";
import { VueFire, VueFireAppCheck, VueFireAuthOptionsFromAuth } from "vuefire";
import { z } from "zod";
import { de as zodDe, en as zodEn, es as zodEs, fr as zodFr, nl as zodNl } from "zod/v4/locales";
import App from "./App.vue";
import { auth, firebaseApp } from "./firebase.js";
import { i18n } from "./i18n/index.js";
import router from "./router/index.js";
import pinia from "./store/index.js";
import "./styles/tailwind.css";
import { getLocale } from "./utils/locale.js";
import vuetify from "./vuetify.js";

const zodLocales = {
  fr: zodFr,
  en: zodEn,
  de: zodDe,
  nl: zodNl,
  es: zodEs,
};

window.addEventListener("vite:preloadError", () => {
  console.log("Preload error detected, asking user to reload");
  if (window.confirm(i18n.global.t("common.newVersionReload"))) {
    console.log("User accepted to reload the page");
    window.location.reload();
  } else {
    console.log("User declined to reload the page");
  }
});

z.config(zodLocales[getLocale()]());

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const app = createApp(App);

if (import.meta.env.PROD) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: import.meta.env.VITE_SENTRY_ENV === "production",
    environment: import.meta.env.VITE_SENTRY_ENV,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
        },
      }),
      Sentry.browserTracingIntegration({ router }),
      Sentry.consoleLoggingIntegration({ levels: ["info", "log", "warn", "error"] }),
      Sentry.replayIntegration(),
    ],
    beforeSend(event, hint) {
      if (isExpectedUserError(hint.originalException) || isExpectedUserSentryEvent(event))
        return null;
      const causeFingerprint = errorCauseFingerprint(hint.originalException);
      if (causeFingerprint) event.fingerprint = ["{{ default }}", causeFingerprint];
      return event;
    },
  });
}

app
  .use(vuetify)
  .use(router)
  .use(pinia)
  .use(VueFire, {
    firebaseApp,
    modules: [
      // Reuse the Auth instance from firebase.ts (emulator connected there).
      // VueFireAuth() would call initializeAuth() again after getAuth().
      VueFireAuthOptionsFromAuth({ auth }),
      VueFireAppCheck({
        provider: new ReCaptchaEnterpriseProvider(
          import.meta.env.VITE_FIREBASE_APPCHECK_RECAPTCHA_KEY,
        ),
        isTokenAutoRefreshEnabled: true,
        debug:
          (import.meta.env.DEV || import.meta.env.VITE_FIREBASE_PROJECT_ID?.startsWith("demo-")) &&
          import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN,
      }),
    ],
  })
  .use(i18n)
  .mount("#app");

// Auto-import all views for dev to solve dynamic imported module issues
if (import.meta.env.DEV) {
  import.meta.glob("@/views/**/*.vue", { eager: true });
}
